import { Injectable } from '@angular/core';
import { PaginationRequest } from 'app/business/common/paginationrequest';
import { extractMasterPage } from 'app/helper/masterpagehelper';
import { MasterService } from 'app/service/master.service';
import { SupplementCategory } from 'app/models/master/supplement-category';
import {
  SupplementMainSubCategoryMapping,
  SupplementSubSubCategoryMapping,
} from 'app/models/master/supplement-category-mapping';
import { SupplementCategoryService } from 'app/service/supplement-category.service';
import {
  getMetadataSectionList,
  mapClaimTags,
  mapClaimTypes,
  mapFssaiNutrientsFromMetadata,
  mapMetadataCategories,
  mapMetadataChildCategories,
  mapNutrientGroups,
  mapNutrientTags,
  mapUnitDosageMastersFromMetadata,
  normalizeSelectableOptions,
  refreshShelfLifeUnitOptions,
} from './product-metadata.util';
import { mapMicroNutritionParamsFromMetadata, MicroNutritionRow } from './micro-nutrition.util';
import { splitFssaiNutrientsByType } from './fssai-nutrient.util';
import { partitionFssaiHierarchy } from './fssai-domain.util';

export const SUPPLEMENT_PRODUCT_METADATA_INCLUDE =
  'recipeLinkSources,ingredientItemTypes,countries,tastes,' +
  'supplementClaimTypes,supplementClaimTags,ingredientClaimTypes,ingredientClaimTags,' +
  'nutrientGroups,nutrientTags,' +
  'fssaiCategories,fssaiSubCategories,fssaiSubSubCategories,' +
  'eStoreCategories,eStoreSubCategories,eStoreSubSubCategories,' +
  'supplementProductBenefits,supplementProductBenefitTags,authorizedCertificates,authorizedLicenses,' +
  'allergens,ingredientAllergens,intolerances,intoleranceFree,allergenFree,' +
  'fssaiNutrients,microNutritionParams,unitDosageMasters,supplementAuthorizedLabels';

export const SUPPLEMENT_RECIPE_METADATA_INCLUDE =
  'dietaryPreferences,tastes,flavours,textures,aromas';

type SettledResult<T> =
  | { status: 'fulfilled'; value: T }
  | { status: 'rejected'; reason: unknown };

function settlePromise<T>(promise: Promise<T>): Promise<SettledResult<T>> {
  return promise
    .then((value) => ({ status: 'fulfilled' as const, value }))
    .catch((reason) => ({ status: 'rejected' as const, reason }));
}

/** Prefer first non-empty array so empty `ingredientAllergens: []` does not block `allergens`. */
function firstNonEmptyMetadataList(metadata: any, keys: string[]): any[] {
  for (const key of keys) {
    const list = getMetadataSectionList(metadata, [key]);
    if (Array.isArray(list) && list.length) return list;
  }
  return [];
}

export interface SupplementFormMetadataSnapshot {
  dataSources: any[];
  databaseSources: any[];
  countries: any[];
  countriesOfOrigin: any[];
  claimTypes: any[];
  claimTags: any[];
  ingredientClaimTypes: any[];
  ingredientClaimTags: any[];
  nutrientGroups: any[];
  nutrientTags: any[];
  productBenefits: any[];
  productBenefitTags: any[];
  certificateOptions: any[];
  licenseOptions: any[];
  fssaiCategories: any[];
  allFssaiSubCategories: any[];
  allFssaiSubSubCategories: any[];
  eStoreCategories: any[];
  allEStoreSubCategories: any[];
  allEStoreSubSubCategories: any[];
  dietaryPreferences: any[];
  tasteList: Array<{ id: number; name: string }>;
  flavourList: Array<{ id: number; name: string }>;
  fssaiNutrientOptions: any[];
  macroNutrientOptions: any[];
  microNutrientOptions: any[];
  microNutritionRows: MicroNutritionRow[];
  nutrientUnitOptions: any[];
  supplementAuthorizedLabelOptions: any[];
  allergenMasterList: any[];
  allergenFreeList: any[];
  intoleranceMasterList: any[];
  intoleranceFreeList: any[];
  units: any[];
  shelfLifeUnits: Array<{ id: string; name: string }>;
  supplementFormOptions: any[];
  supplementMainCategories: SupplementCategory[];
  supplementSubCategories: SupplementCategory[];
  supplementSubSubCategories: SupplementCategory[];
  supplementMainSubMappings: SupplementMainSubCategoryMapping[];
  supplementSubSubMappings: SupplementSubSubCategoryMapping[];
  /** Sub-categories under main "Health Condition" — used by Step 16. */
  healthConditionCategories: Array<{ id: string; name: string }>;
  /** Sub-sub categories linked to health condition subs (parentId = subCategoryId). */
  healthConditionTags: Array<{ id: string; name: string; parentId: string }>;
}

@Injectable({ providedIn: 'root' })
export class SupplementFormMetadataLoader {
  constructor(
    private masterService: MasterService,
    private supplementCategoryService: SupplementCategoryService
  ) {}

  async load(): Promise<SupplementFormMetadataSnapshot> {
    const paginationRequest = new PaginationRequest();
    paginationRequest.pageNo = 1;
    paginationRequest.pageSize = 1000;
    paginationRequest.searchString = '';

    const [
      productMetadataResult,
      recipeMetadataResult,
      supplementHierarchyResult,
      mainSubMappingsResult,
      subSubMappingsResult,
      productFormResult,
      unitsResult,
    ] = await Promise.all([
      settlePromise(this.masterService.getProductMetadata(SUPPLEMENT_PRODUCT_METADATA_INCLUDE)),
      settlePromise(this.masterService.getRecipeMetadata(SUPPLEMENT_RECIPE_METADATA_INCLUDE)),
      settlePromise(this.supplementCategoryService.getCategoryHierarchy(true)),
      settlePromise(this.supplementCategoryService.getMainSubCategoryMappings()),
      settlePromise(this.supplementCategoryService.getSubToSubSubCategoryMappings()),
      settlePromise(this.masterService.getProductForm(paginationRequest)),
      settlePromise(this.masterService.getIngredientUnitOfMeasurements(paginationRequest)),
    ]);

    const productMetadata = productMetadataResult.status === 'fulfilled' ? productMetadataResult.value : {};
    const recipeMetadata = recipeMetadataResult.status === 'fulfilled' ? recipeMetadataResult.value : {};
    const supplementHierarchy =
      supplementHierarchyResult.status === 'fulfilled'
        ? supplementHierarchyResult.value
        : { main: [], sub: [], subSub: [] };
    const mainSubMappings =
      mainSubMappingsResult.status === 'fulfilled' ? mainSubMappingsResult.value : [];
    const subSubMappings =
      subSubMappingsResult.status === 'fulfilled' ? subSubMappingsResult.value : [];
    const productFormResponse = productFormResult.status === 'fulfilled' ? productFormResult.value : {};
    const unitsResponse = unitsResult.status === 'fulfilled' ? unitsResult.value : {};

    if (productMetadataResult.status === 'rejected') {
      console.error('Supplement form: product metadata load failed', productMetadataResult.reason);
    }
    if (recipeMetadataResult.status === 'rejected') {
      console.error('Supplement form: recipe metadata load failed', recipeMetadataResult.reason);
    }

    return this.mapSnapshot(
      productMetadata,
      recipeMetadata,
      supplementHierarchy,
      mainSubMappings,
      subSubMappings,
      productFormResponse,
      unitsResponse
    );
  }

  private mapSensoryMetadataOptions(
    recipeMetadata: any,
    productMetadata: any,
    keys: string[]
  ): Array<{ id: number; name: string }> {
    const fromRecipe = normalizeSelectableOptions(getMetadataSectionList(recipeMetadata, keys));
    const fromProduct = normalizeSelectableOptions(getMetadataSectionList(productMetadata, keys));
    const source = fromRecipe.length ? fromRecipe : fromProduct;

    return source
      .map((item) => ({
        id: Number(item.id),
        name: String(item.name ?? '').trim(),
      }))
      .filter((item) => !Number.isNaN(item.id) && !!item.name);
  }

  private mapSnapshot(
    productMetadata: any,
    recipeMetadata: any,
    supplementHierarchy: {
      main: SupplementCategory[];
      sub: SupplementCategory[];
      subSub: SupplementCategory[];
    },
    mainSubMappings: SupplementMainSubCategoryMapping[],
    subSubMappings: SupplementSubSubCategoryMapping[],
    productFormResponse: any,
    unitsResponse: any
  ): SupplementFormMetadataSnapshot {
    const units = extractMasterPage(unitsResponse).items.filter((item: any) => item?.isActive !== false);
    const supplementFormOptions = normalizeSelectableOptions(extractMasterPage(productFormResponse).items);

    const claimTypes = mapClaimTypes(
      getMetadataSectionList(productMetadata, [
        'supplementClaimTypes',
        'SupplementClaimTypes',
        'claimTypes',
        'ClaimTypes',
      ])
    );
    const claimTags = mapClaimTags(
      getMetadataSectionList(productMetadata, [
        'supplementClaimTags',
        'SupplementClaimTags',
        'claimTags',
        'ClaimTags',
      ])
    );
    const fssaiNutrientOptions = mapFssaiNutrientsFromMetadata(
      getMetadataSectionList(productMetadata, ['fssaiNutrients', 'FssaiNutrients'])
    );
    const { macro, micro } = splitFssaiNutrientsByType(fssaiNutrientOptions);
    const microNutritionRows = mapMicroNutritionParamsFromMetadata(
      getMetadataSectionList(productMetadata, ['microNutritionParams', 'MicroNutritionParams'])
    );
    const nutrientUnitOptions = mapUnitDosageMastersFromMetadata(productMetadata);
    const supplementAuthorizedLabelOptions = normalizeSelectableOptions(
      getMetadataSectionList(productMetadata, ['supplementAuthorizedLabels', 'SupplementAuthorizedLabels'])
    );

    const allergenContains = normalizeSelectableOptions(
      firstNonEmptyMetadataList(productMetadata, [
        'ingredientAllergens', 'IngredientAllergens', 'allergens', 'Allergens',
        'allergenContains', 'AllergenContains',
      ])
    ).map((item) => ({ ...item, allergenType: 1 }));

    const allergenFree = normalizeSelectableOptions(
      firstNonEmptyMetadataList(productMetadata, ['allergenFree', 'AllergenFree', 'freeAllergens', 'FreeAllergens'])
    ).map((item) => ({ ...item, allergenType: 2 }));

    const intoleranceContains = normalizeSelectableOptions(
      getMetadataSectionList(productMetadata, [
        'intolerances', 'Intolerances', 'ingredientIntolerances', 'IngredientIntolerances',
      ])
    ).map((item) => ({ ...item, intoleranceType: 1 }));

    const intoleranceFree = normalizeSelectableOptions(
      getMetadataSectionList(productMetadata, [
        'intoleranceFree', 'IntoleranceFree', 'freeIntolerances', 'FreeIntolerances',
      ])
    ).map((item) => ({ ...item, intoleranceType: 2 }));

    const countries = normalizeSelectableOptions(
      getMetadataSectionList(productMetadata, ['countries', 'Countries'])
    );

    const healthConditionMain = (supplementHierarchy.main || []).find((item) =>
      String(item.name || '')
        .trim()
        .toLowerCase()
        .includes('health condition')
    );
    const healthMainId = healthConditionMain?.id != null ? String(healthConditionMain.id) : '';
    const healthSubIds = new Set(
      (mainSubMappings || [])
        .filter((m) => String(m.mainCategoryId) === healthMainId)
        .map((m) => String(m.subCategoryId))
    );
    const healthConditionCategories = (supplementHierarchy.sub || [])
      .filter((item) => healthSubIds.has(String(item.id)))
      .map((item) => ({ id: String(item.id), name: item.name }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const subById = new Map((supplementHierarchy.subSub || []).map((item) => [String(item.id), item]));
    const healthConditionTags = (subSubMappings || [])
      .filter((m) => healthSubIds.has(String(m.subCategoryId)))
      .map((m) => {
        const tag = subById.get(String(m.subSubCategoryId));
        const name = (tag?.name || m.subSubCategoryName || '').trim();
        if (!name) {
          return null;
        }
        return {
          id: String(m.subSubCategoryId),
          name,
          parentId: String(m.subCategoryId),
        };
      })
      .filter((item): item is { id: string; name: string; parentId: string } => !!item)
      .sort((a, b) => a.name.localeCompare(b.name));

    return {
      dataSources: normalizeSelectableOptions(
        getMetadataSectionList(productMetadata, ['ingredientItemTypes', 'IngredientItemTypes'])
      ),
      databaseSources: normalizeSelectableOptions(
        getMetadataSectionList(productMetadata, ['recipeLinkSources', 'RecipeLinkSources'])
      ),
      countries,
      countriesOfOrigin: countries.length ? countries : [],
      claimTypes,
      claimTags,
      ingredientClaimTypes: mapClaimTypes(productMetadata?.ingredientClaimTypes ?? []),
      ingredientClaimTags: mapClaimTags(productMetadata?.ingredientClaimTags ?? []),
      nutrientGroups: mapNutrientGroups(
        getMetadataSectionList(productMetadata, ['nutrientGroups', 'NutrientGroups'])
      ),
      nutrientTags: mapNutrientTags(
        getMetadataSectionList(productMetadata, ['nutrientTags', 'NutrientTags'])
      ),
      productBenefits: normalizeSelectableOptions(
        getMetadataSectionList(productMetadata, [
          'supplementProductBenefits',
          'SupplementProductBenefits',
          'productBenefits',
          'ProductBenefits',
        ])
      ),
      productBenefitTags: normalizeSelectableOptions(
        getMetadataSectionList(productMetadata, [
          'supplementProductBenefitTags',
          'SupplementProductBenefitTags',
          'productBenefitTags',
          'ProductBenefitTags',
        ])
      ).map((tag) => ({
        ...tag,
        productBenefitId: String(tag.parentId ?? tag.productBenefitId ?? ''),
      })),
      certificateOptions: normalizeSelectableOptions(productMetadata?.authorizedCertificates ?? []),
      licenseOptions: normalizeSelectableOptions(productMetadata?.authorizedLicenses ?? []),
      ...(() => {
        const partitioned = partitionFssaiHierarchy(
          mapMetadataCategories(
            getMetadataSectionList(productMetadata, ['fssaiCategories', 'FssaiCategories'])
          ),
          mapMetadataChildCategories(
            getMetadataSectionList(productMetadata, ['fssaiSubCategories', 'FssaiSubCategories']),
            'categoryId'
          ),
          mapMetadataChildCategories(
            getMetadataSectionList(productMetadata, ['fssaiSubSubCategories', 'FssaiSubSubCategories']),
            'subCategoryId'
          ),
          'supplement'
        );
        return {
          fssaiCategories: partitioned.categories,
          allFssaiSubCategories: partitioned.subCategories,
          allFssaiSubSubCategories: partitioned.subSubCategories,
        };
      })(),
      eStoreCategories: mapMetadataCategories(
        getMetadataSectionList(productMetadata, ['eStoreCategories', 'EStoreCategories'])
      ),
      allEStoreSubCategories: mapMetadataChildCategories(
        getMetadataSectionList(productMetadata, ['eStoreSubCategories', 'EStoreSubCategories']),
        'categoryId'
      ),
      allEStoreSubSubCategories: mapMetadataChildCategories(
        getMetadataSectionList(productMetadata, ['eStoreSubSubCategories', 'EStoreSubSubCategories']),
        'subCategoryId'
      ),
      dietaryPreferences: normalizeSelectableOptions(
        getMetadataSectionList(recipeMetadata, ['dietaryPreferences', 'DietaryPreferences'])
      ),
      tasteList: this.mapSensoryMetadataOptions(recipeMetadata, productMetadata, ['tastes', 'Tastes']),
      flavourList: this.mapSensoryMetadataOptions(recipeMetadata, productMetadata, ['flavours', 'Flavours']),
      fssaiNutrientOptions,
      macroNutrientOptions: macro,
      microNutrientOptions: micro,
      microNutritionRows,
      nutrientUnitOptions,
      supplementAuthorizedLabelOptions,
      allergenMasterList: allergenContains,
      allergenFreeList: allergenFree,
      intoleranceMasterList: intoleranceContains,
      intoleranceFreeList: intoleranceFree,
      units,
      shelfLifeUnits: refreshShelfLifeUnitOptions(units),
      supplementFormOptions,
      supplementMainCategories: supplementHierarchy.main,
      supplementSubCategories: supplementHierarchy.sub,
      supplementSubSubCategories: supplementHierarchy.subSub,
      supplementMainSubMappings: mainSubMappings || [],
      supplementSubSubMappings: subSubMappings || [],
      healthConditionCategories,
      healthConditionTags,
    };
  }
}
